import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, ShieldCheck, Zap, HeartHandshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { PoweredBy } from '@/components/common/PoweredBy';
import { SharitiLockup, SharitiMark } from '@/components/common/SharitiLogo';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Bienvenue sur SHARITI');
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[1.05fr_1fr]">
      {/* Immersive brand panel */}
      <div className="shariti-hero relative hidden flex-col justify-between overflow-hidden p-12 lg:flex">
        <div className="hero-grain absolute inset-0 opacity-60" />
        {/* Abstract engagement visual */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-black/10 blur-2xl" />
          <SharitiMark white className="floaty absolute -bottom-10 right-6 h-80 w-80 opacity-20" />
          <div className="absolute right-24 top-28 h-24 w-24 rounded-3xl border border-white/20 bg-white/5 backdrop-blur-sm floaty" style={{ animationDelay: '1.2s' }} />
        </div>

        <div className="relative z-10">
          <SharitiLockup white size="lg" />
        </div>

        <div className="relative z-10 max-w-md text-white">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur">
            <HeartHandshake className="h-3.5 w-3.5" /> Face-to-Face Fundraising
          </div>
          <h1 className="font-display text-[2.6rem] font-semibold leading-[1.1] tracking-tight">
            La plateforme de pilotage qui donne de l'élan à vos campagnes.
          </h1>
          <p className="mt-4 text-white/85">
            Suivez vos objectifs, vos performances terrain et vos donateurs en temps réel.
            Une expérience premium pensée pour l'impact.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 text-sm">
            <Chip icon={ShieldCheck} label="Données sécurisées" />
            <Chip icon={Zap} label="Temps réel" />
          </div>
        </div>

        <div className="relative z-10">
          <PoweredBy white className="text-white/70" />
        </div>
      </div>

      {/* Login card */}
      <div className="app-bg flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-6 flex justify-center lg:hidden">
            <SharitiLockup size="md" />
          </div>
          <div className="rounded-3xl border glass-strong card-shadow-lg p-8">
            <div className="mb-6 hidden lg:flex"><SharitiMark className="h-11 w-11" /></div>
            <h2 className="font-display text-2xl font-semibold tracking-tight">Connexion</h2>
            <p className="mt-1 text-sm text-muted-foreground">Accédez à votre portail de pilotage.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@association.org" required data-testid="login-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input id="password" type={show ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
                    data-testid="login-password-input" />
                  <button type="button" onClick={() => setShow(!show)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full gap-2 rounded-xl"
                style={{ background: 'var(--tenant-accent-500)' }} data-testid="login-submit-button">
                {loading ? 'Connexion…' : <>Se connecter <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </form>
            <div className="my-4 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" /><span className="text-[11px] text-muted-foreground">ou</span><span className="h-px flex-1 bg-border" />
            </div>
            <Button variant="outline" className="w-full gap-2 rounded-xl" data-testid="login-google-button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}>
              <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              Continuer avec Google
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Chip = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-white/90 backdrop-blur">
    <Icon className="h-4 w-4" /> {label}
  </div>
);
